import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, ChevronRight, Building2, Home } from 'lucide-react';
import Layout from '@/components/Layout';
import PageHeader from '@/components/PageHeader';
import CtaBand from '@/components/CtaBand';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { api, fileUrl } from '@/lib/api';

export default function ServicesPage() {
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => { api.get('/services').then(r => setCats(r.data.categories || [])); }, []);

  const filtered = useMemo(() => {
    let list = cats;
    if (filter === 'residential') list = list.filter(c => c.slug !== 'commercial');
    if (filter === 'commercial') list = list.filter(c => c.slug === 'commercial');
    if (!q.trim()) return list;
    const query = q.toLowerCase();
    return list.map(c => ({
      ...c,
      sub_services: c.sub_services.filter(s => s.name.toLowerCase().includes(query) || c.name.toLowerCase().includes(query)),
    })).filter(c => c.sub_services.length > 0);
  }, [cats, q, filter]);

  return (
    <Layout>
      <PageHeader
        eyebrow="Browse All Services"
        title="Every service you need, under one roof."
        subtitle="From a single furniture-assembly visit to a full luxury kitchen remodel — select a service, get a transparent estimate, and schedule with a trusted pro."
      >
        <div className="flex flex-col sm:flex-row gap-3 max-w-2xl">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="services-category-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search 170+ services (e.g. impact windows, pressure washing)"
              className="pl-10 h-12 rounded-xl bg-white"
            />
          </div>
          <div className="inline-flex bg-white border border-[#E6E9EE] rounded-xl p-1 self-start">
            {[
              { v: 'all', label: 'All', icon: null },
              { v: 'residential', label: 'Residential', icon: Home },
              { v: 'commercial', label: 'Commercial', icon: Building2 },
            ].map(o => (
              <button key={o.v}
                data-testid={`services-filter-${o.v}`}
                onClick={() => setFilter(o.v)}
                className={`px-3 h-10 inline-flex items-center gap-1.5 rounded-lg text-sm transition-colors-only ${filter === o.v ? 'bg-[#17352D] text-white' : 'text-slate-600 hover:bg-[#F2F7F5]'}`}>
                {o.icon ? <o.icon className="h-3.5 w-3.5" /> : null} {o.label}
              </button>
            ))}
          </div>
        </div>
      </PageHeader>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8">
          {/* Sidebar quick jump */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 rounded-2xl border border-[#E6E9EE] bg-white p-4">
              <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-3">Categories</div>
              <ul className="space-y-1">
                {filtered.map((c) => (
                  <li key={c.slug}>
                    <a href={`#cat-${c.slug}`} className="block px-2 py-1.5 text-sm rounded-lg text-slate-700 hover:bg-[#F2F7F5] hover:text-[#17352D]">{c.name}</a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          <div data-testid="services-category-accordion">
            <Accordion type="multiple" defaultValue={filtered.slice(0, 2).map(c => c.slug)} className="space-y-3">
              {filtered.map((c) => (
                <AccordionItem key={c.slug} value={c.slug} id={`cat-${c.slug}`} data-testid="services-category-item" className="rounded-2xl border border-[#E6E9EE] bg-white px-4 md:px-5">
                  <AccordionTrigger className="py-4 md:py-5 hover:no-underline">
                    <div className="flex items-center gap-4 text-left">
                      <img src={fileUrl(c.image)} alt={c.name} loading="lazy" className="hidden sm:block h-14 w-20 rounded-lg object-cover border border-[#E6E9EE]" />
                      <div>
                        <div className="font-headline text-xl md:text-2xl text-[#17352D]">{c.name}</div>
                        <div className="text-sm text-slate-600 mt-0.5">{c.tagline}</div>
                      </div>
                      <span className="ml-auto hidden md:inline-flex text-xs px-2.5 py-1 rounded-full bg-[#F2F7F5] text-[#17352D] border border-[#C7DED4]">{c.sub_services.length} services</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pb-3">
                      {c.sub_services.map((s) => (
                        <div key={s.slug} className="flex items-center justify-between gap-3 rounded-xl border border-[#E6E9EE] bg-[#F7F8FA] hover:bg-white p-3 transition-colors-only">
                          <div className="min-w-0">
                            <Link to={`/services/${c.slug}#${s.slug}`} data-testid="services-subservice-link" className="font-medium text-[#17352D] hover:underline truncate block">{s.name}</Link>
                            <div className="text-xs text-slate-500 mt-0.5">From ${s.price_low.toLocaleString()}{s.price_high > s.price_low && <> – ${s.price_high.toLocaleString()}</>} / {s.unit}</div>
                          </div>
                          <Button asChild size="sm" data-testid="services-subservice-get-estimate-button" className="rounded-lg bg-[#C8922F] hover:bg-[#A87424] text-white">
                            <Link to={`/estimate?category=${c.slug}&sub=${s.slug}`}>Get Estimate</Link>
                          </Button>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between border-t border-[#E6E9EE] pt-3 pb-1">
                      <Link to={`/services/${c.slug}`} className="text-sm font-semibold text-[#17352D] inline-flex items-center gap-1 hover:gap-2 transition-all">
                        View {c.name} details <ChevronRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
              {filtered.length === 0 && (
                <div className="text-center py-12 text-slate-500">No services match "{q}". Try a different keyword.</div>
              )}
            </Accordion>
          </div>
        </div>
      </section>

      <CtaBand />
    </Layout>
  );
}
